/**
 * ORA - AI Leadership Agent
 * Copyright (c) 2024-2026 s2cpaul
 * Licensed under MIT License
 * Repository: https://github.com/s2cpaul/ora-agent
 */

export interface ScriptSubmission {
  content: string;
  submittedBy: string; // email of submitter
  submittedAt: number; // timestamp
  wordCount: number;
}

export interface OrderData {
  id?: string;
  userId: string;
  userEmail: string;
  orderType: 'subscription' | 'alacarte';
  planName?: string;
  productName?: string;
  amount: number;
  status?: 'delivered' | 'pending' | 'failed';
  paymentMethod: string;
  transactionId: string;
  script?: string; // Legacy single script (deprecated)
  scripts?: ScriptSubmission[]; // New: Support up to 6 scripts with metadata
  customerId?: string;
  mediaType?: 'photo' | 'voice' | 'none';
  mediaUrl?: string;
  voiceMediaUrl?: string;
  imageUrl?: string;
  avatarId?: string;
  personTemplate?: string;
}

/**
 * Adds an order to the order history stored in localStorage
 * This function should be called whenever a payment is completed
 */
export function addOrderToHistory(orderData: OrderData): void {
  try {
    // Generate order ID if not provided
    const orderId = orderData.id || `ORD-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
    
    // Create the complete order object
    const order = {
      id: orderId,
      userId: orderData.userId,
      userEmail: orderData.userEmail,
      timestamp: Date.now(),
      orderType: orderData.orderType,
      planName: orderData.planName,
      productName: orderData.productName,
      amount: orderData.amount,
      status: orderData.status || 'pending',
      paymentMethod: orderData.paymentMethod,
      transactionId: orderData.transactionId,
      script: orderData.script,
      scripts: orderData.scripts,
      customerId: orderData.customerId,
      mediaType: orderData.mediaType,
      mediaUrl: orderData.mediaUrl,
      voiceMediaUrl: orderData.voiceMediaUrl,
      imageUrl: orderData.imageUrl,
      avatarId: orderData.avatarId,
      personTemplate: orderData.personTemplate,
    };

    // Get existing orders from localStorage
    const existingOrders = localStorage.getItem('orderHistory');
    const orders = existingOrders ? JSON.parse(existingOrders) : [];

    // Add new order to the beginning of the array
    orders.unshift(order);

    // Save back to localStorage
    localStorage.setItem('orderHistory', JSON.stringify(orders));

    console.log('Order added to history:', orderId);
  } catch (error) {
    console.error('Error adding order to history:', error);
  }
}

/**
 * Retrieves all orders from order history
 */
export function getOrderHistory(): OrderData[] {
  try {
    const stored = localStorage.getItem('orderHistory');
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error retrieving order history:', error);
    return [];
  }
}

/**
 * Updates an existing order's status
 */
export function updateOrderStatus(orderId: string, status: 'delivered' | 'pending' | 'failed'): void {
  try {
    const stored = localStorage.getItem('orderHistory');
    if (!stored) return;

    const orders = JSON.parse(stored);
    const updatedOrders = orders.map((order: OrderData) => {
      if (order.id === orderId) {
        return { ...order, status };
      }
      return order;
    });

    localStorage.setItem('orderHistory', JSON.stringify(updatedOrders));
    console.log(`Order ${orderId} status updated to ${status}`);
  } catch (error) {
    console.error('Error updating order status:', error);
  }
}